import type { CapacityRange, TalosCapacityResponse } from '@pulse/contracts';
import * as React from 'react';

import { useTalosCapacity } from '@/domains/talos/api';

/** A point's width across the chart's 1000 units: a 5-minute sample over 24 hours, an hour over 7 days. */
const POINT_WIDTH: Readonly<Record<CapacityRange, number>> = { day: (1000 * 5) / (24 * 60), week: 1000 / (7 * 24) };
const y = (percent: number) => (180 - Math.min(100, Math.max(0, percent)) * 1.8).toFixed(1);

/** The line through each run of points, broken where samples are missing. */
function segments(points: TalosCapacityResponse['points']): Array<TalosCapacityResponse['points']> {
  const runs: Array<TalosCapacityResponse['points']> = [];
  for (const point of points) {
    if (point.gapBefore || runs.length === 0) runs.push([]);
    runs[runs.length - 1]?.push(point);
  }
  return runs;
}

/** Capacity on the Talos page (TLS-10): fetches its range and draws it. */
export function CapacityCard(): React.ReactElement {
  const [range, setRange] = React.useState<CapacityRange>('day');
  const capacity = useTalosCapacity(range);
  return <CapacityView data={capacity.data ?? null} range={range} onRange={setRange} />;
}

/** Built from the prototype's `capacityCard()`: the verdict, four cells, the chart and the runs strip. */
export function CapacityView({ data, range, onRange }: { readonly data: TalosCapacityResponse | null; readonly range: CapacityRange; readonly onRange: (range: CapacityRange) => void }): React.ReactElement {
  const width = POINT_WIDTH[range];
  const points = data?.points ?? [];
  const mostRuns = Math.max(0, ...points.map((point) => point.running.length));
  const most = Math.max(1, mostRuns);
  const peakCpu = Math.max(0, ...points.map((point) => point.cpu));
  const peakMemory = Math.max(0, ...points.map((point) => point.memory));
  const line = (key: 'cpu' | 'memory') => segments(points).map((run) => run.map((point) => `${(point.position * 1000).toFixed(1)},${y(point[key])}`).join(' '));
  return (
    <section className="card pad stack" style={{ gap: 14 }} aria-labelledby="caph">
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className="stack" style={{ gap: 2 }}>
          <h2 id="caph">Capacity</h2>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            The Mini&apos;s CPU and memory, sampled every 5 minutes{range === 'week' ? "; each point is an hour's average" : ''}.{data?.sampledLabel ? ` ${data.sampledLabel}` : ''}
          </span>
        </div>
        <div className="seg" role="group" aria-label="Capacity range">
          <button type="button" aria-pressed={range === 'day'} onClick={() => onRange('day')}>
            24 hours
          </button>
          <button type="button" aria-pressed={range === 'week'} onClick={() => onRange('week')}>
            7 days
          </button>
        </div>
      </div>
      {data === null ? null : <span style={{ color: 'var(--ink-2)' }}>{data.verdict}</span>}
      {data !== null && data.reported ? (
        <>
          <div className="health">
            {data.cells.map((cell) => (
              <div key={cell.label} className={`hcell${cell.warn ? ' warn' : ''}`}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{cell.label}</span>
                <span style={{ fontWeight: 600, color: cell.warn ? 'var(--warn-ink)' : 'var(--ink)' }}>{cell.value}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{cell.detail}</span>
              </div>
            ))}
          </div>
          <div className="cap-legend">
            <span>
              <i style={{ background: 'var(--accent)' }} />
              CPU
            </span>
            <span>
              <i style={{ background: 'var(--info-ink)' }} />
              Memory used
            </span>
            <span>
              <i className="band" />
              At capacity
            </span>
            <span>
              <i style={{ background: 'var(--s-prog)', height: 10 }} />
              Runs in progress
            </span>
          </div>
          <div className="chart" role="img" aria-label={`CPU and memory over the last ${range === 'day' ? '24 hours' : '7 days'}: CPU peaks at ${peakCpu}%, memory at ${peakMemory}%`}>
            <div className="yaxis num" style={{ height: 180 }}>
              {['100%', '75%', '50%', '25%', '0'].map((tick) => (
                <span key={tick}>{tick}</span>
              ))}
            </div>
            <div className="cap-plot">
              <svg viewBox="0 0 1000 180" preserveAspectRatio="none" aria-hidden="true">
                {points.map((point) =>
                  point.full ? <rect key={point.position} className="full" x={(point.position * 1000 - width / 2).toFixed(1)} y={0} width={width.toFixed(2)} height={180} fill="var(--warn-ink)" opacity={0.16} /> : null,
                )}
                <line x1={0} x2={1000} y1={y(90)} y2={y(90)} stroke="var(--warn-ink)" strokeDasharray="6 6" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                {line('memory').map((path) => (
                  <polyline key={`m${path}`} className="memory" points={path} fill="none" stroke="var(--info-ink)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                ))}
                {line('cpu').map((path) => (
                  <polyline key={`c${path}`} className="cpu" points={path} fill="none" stroke="var(--accent)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
            </div>
          </div>
          <div className="chart">
            <span style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'right', alignSelf: 'center' }}>Runs</span>
            <div className="cap-runs">
              <svg viewBox="0 0 1000 28" preserveAspectRatio="none" role="img" aria-label={`Runs in progress: at most ${mostRuns} at once`}>
                {points.map((point) =>
                  point.running.length === 0 ? null : (
                    <rect key={point.position} x={(point.position * 1000 - width / 2).toFixed(1)} y={(28 - (point.running.length / most) * 26).toFixed(1)} width={Math.max(width - 0.6, 0.8).toFixed(2)} height={((point.running.length / most) * 26).toFixed(1)} fill="var(--s-prog)">
                      <title>{`${point.label}: ${point.running.join(', ')}`}</title>
                    </rect>
                  ),
                )}
              </svg>
            </div>
          </div>
          <div className="cap-x" aria-hidden="true">
            {data.ticks.map((tick) => (
              <span key={`${tick.label}${tick.position}`} style={{ left: `${(tick.position * 100).toFixed(2)}%` }}>
                {tick.label}
              </span>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
